// Input adaptation for the laptop desktop host: key maps, the touch wire
// format, pointer-to-app coordinate mapping, intent validation and the theme
// the host passes between the System UI, the apps and the viewer's storage.
// No DOM and no realm access, so bun test covers it without a browser.

import { appTitles, type Lang } from "../../desktop/apps/shared/words.ts";
import { LAPTOP_PROGRAMS, type DesktopThemeId, type LaptopProgram, type StudioIntentLine, type StudioStateLine } from "../core/protocol.ts";
import { DEVICE_IDS, type DeviceId } from "../core/types.ts";

/** Companion service name the host grants to every Studio app. */
export const STUDIO_SERVICE = "studio";

/** Host-private line on the "studio" channel: wheel travel over an app. */
export interface DeskWheelLine {
  t: "wheel";
  dy: number;
}

/** Everything the host writes to a Studio app. */
export type DeskLine = StudioStateLine | DeskWheelLine;

/** Controller bits, spec BTN. */
export const BTN = {
  SELECT: 0x0001,
  START: 0x0008,
  UP: 0x0010,
  RIGHT: 0x0020,
  DOWN: 0x0040,
  LEFT: 0x0080,
  LTRIGGER: 0x0100,
  RTRIGGER: 0x0200,
  TRIANGLE: 0x1000,
  CIRCLE: 0x2000,
  CROSS: 0x4000,
  SQUARE: 0x8000,
} as const;

/** KeyboardEvent.code to controller bit. CIRCLE confirms in the focus manager. */
export const KEY_BUTTONS: Readonly<Record<string, number>> = {
  ArrowUp: BTN.UP,
  ArrowRight: BTN.RIGHT,
  ArrowDown: BTN.DOWN,
  ArrowLeft: BTN.LEFT,
  Enter: BTN.CIRCLE,
  KeyZ: BTN.CIRCLE,
  KeyX: BTN.CROSS,
  KeyA: BTN.SQUARE,
  KeyS: BTN.TRIANGLE,
  KeyQ: BTN.LTRIGGER,
  KeyW: BTN.RTRIGGER,
  KeyE: BTN.RTRIGGER,
  ShiftLeft: BTN.SELECT,
  ShiftRight: BTN.SELECT,
  Space: BTN.START,
};

/** KeyboardEvent.key to the System UI's named keys. */
const NAMED_KEYS: Readonly<Record<string, string>> = {
  Backspace: "Backspace",
  Delete: "Delete",
  Enter: "Enter",
  Tab: "Tab",
  ArrowLeft: "Left",
  ArrowRight: "Right",
  ArrowUp: "Up",
  ArrowDown: "Down",
  Home: "Home",
  End: "End",
  PageUp: "PageUp",
  PageDown: "PageDown",
  Escape: "Escape",
  F1: "F1",
  F2: "F2",
  F3: "F3",
  F4: "F4",
  F5: "F5",
  F6: "F6",
  F7: "F7",
  F8: "F8",
  F9: "F9",
  F10: "F10",
  F11: "F11",
  F12: "F12",
};

/** The KeyboardEvent fields the host reads. A DOM KeyboardEvent satisfies it. */
export interface KeyLike {
  key: string;
  code: string;
  shiftKey?: boolean;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
}

/** One line of the System UI input protocol (shells/desktop svc.ts). */
export type ShellLine =
  | { t: "hello"; w: number; h: number; epoch: number }
  | { t: "mouse"; x: number; y: number; d: boolean; b?: 2; sh?: boolean }
  | { t: "scroll"; dy: number }
  | { t: "key"; k: string; cmd?: true; sh: boolean; alt: boolean; ctl: boolean }
  | { t: "ch"; s: string }
  | { t: "paste"; text: string }
  /** Open the installed app, or restore, raise and focus its window. */
  | { t: "open"; package: string }
  /** Select the desktop theme. */
  | { t: "theme"; id: DesktopThemeId }
  /** The language the System UI draws in, and the Studio apps' titles in it by package id. */
  | { t: "lang"; id: Lang; titles: Record<string, string> };

// ---- language -------------------------------------------------------------

/** The line that tells the System UI the language: its own words, and the Studio apps' titles. */
export function langLine(lang: Lang): ShellLine {
  return { t: "lang", id: lang, titles: appTitles(lang) };
}

// ---- theme --------------------------------------------------------------

/** The System UI's theme ids, in its picker order. */
export const DESKTOP_THEMES: readonly DesktopThemeId[] = ["classic", "xp", "aqua"];

/** Theme of a viewer who has not chosen one. */
export const DEFAULT_THEME: DesktopThemeId = "aqua";

/** localStorage key of the viewer's theme. */
export const THEME_STORAGE_KEY = "pocket-studio.desktop.theme";

export function isDesktopTheme(value: unknown): value is DesktopThemeId {
  return typeof value === "string" && (DESKTOP_THEMES as readonly string[]).includes(value);
}

/** The theme a System UI line reports, or null for any other line. */
export function reportedTheme(message: { t?: unknown; id?: unknown }): DesktopThemeId | null {
  return message.t === "theme" && isDesktopTheme(message.id) ? message.id : null;
}

/** The state line an app receives: the page's line with the active theme added. */
export function withTheme(line: StudioStateLine, theme: DesktopThemeId): StudioStateLine {
  return { ...line, theme };
}

/** What the page asked one app to show: StudioStateLine's `show`. */
export type ShowRequest = NonNullable<StudioStateLine["show"]>;

/**
 * The state line one app receives: the page's line with the active theme
 * added, and the page's request of that app when it made one. A `show` on the
 * page's own line is dropped: a request is made with `StudioDesktop.launch`,
 * for one app.
 */
export function lineFor(line: StudioStateLine, theme: DesktopThemeId, show: ShowRequest | undefined): StudioStateLine {
  const { show: _dropped, ...sent } = withTheme(line, theme);
  return show ? { ...sent, show } : sent;
}

/** The part of Storage the theme needs. */
export interface ThemeStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The viewer's stored theme. Null when there is none, the value is no theme or the store throws. */
export function readStoredTheme(store: ThemeStore | null | undefined): DesktopThemeId | null {
  try {
    const value = store?.getItem(THEME_STORAGE_KEY);
    return isDesktopTheme(value) ? value : null;
  } catch {
    return null;
  }
}

/** Keep the viewer's theme. Returns false when there is no store or it refuses the write. */
export function storeTheme(store: ThemeStore | null | undefined, theme: DesktopThemeId): boolean {
  try {
    if (!store) return false;
    store.setItem(THEME_STORAGE_KEY, theme);
    return true;
  } catch {
    return false;
  }
}

/**
 * Add one app to the launches a paused desktop holds until it resumes. Each
 * app appears once, and the app asked for last moves to the end, so it is the
 * one left focused after the lines are sent in order.
 */
export function queueLaunch(pending: readonly string[], packageId: string): string[] {
  return [...pending.filter((id) => id !== packageId), packageId];
}

export type KeyAction =
  /** Hold a controller bit for the focused app. */
  | { kind: "button"; bit: number }
  /** Read the clipboard and send it to the System UI as a paste line. */
  | { kind: "paste" }
  | { kind: "shell"; line: ShellLine };

/**
 * Route one key press. With an app focused, mapped keys become controller
 * buttons; everything else goes to the System UI as chords, named keys or
 * typed characters. Null means the desktop has no use for the key.
 */
export function routeKey(event: KeyLike, appFocused: boolean): KeyAction | null {
  const shift = event.shiftKey ?? false;
  const alt = event.altKey ?? false;
  const ctrl = event.ctrlKey ?? false;
  const command = (event.metaKey ?? false) || ctrl;
  const bit = KEY_BUTTONS[event.code];
  if (appFocused && bit !== undefined && !command && !alt) return { kind: "button", bit };
  if (command) {
    const key = event.key.toLowerCase();
    if (key === "v") return { kind: "paste" };
    // Bare modifier presses carry no chord.
    if (key === "meta" || key === "control" || key === "shift" || key === "alt") return null;
    return { kind: "shell", line: { t: "key", k: key, cmd: true, sh: shift, alt, ctl: ctrl } };
  }
  const named = NAMED_KEYS[event.key];
  if (named) return { kind: "shell", line: { t: "key", k: named, sh: shift, alt, ctl: ctrl } };
  if (event.key.length === 1 && !alt) return { kind: "shell", line: { t: "ch", s: event.key } };
  return null;
}

// Touch words, the frame() argument 3 wire format (framework/src/touch.ts):
// bit 31 marks the wide form x:10 y:10 id:8; bit 30 is a terminal cancel.
const WIDE_MARKER = 0x80000000;
const CANCEL_MARKER = 0x40000000;
const WIDE_COORD_BITS = 10;
const WIDE_COORD_MASK = (1 << WIDE_COORD_BITS) - 1;

export function packTouchWide(id: number, x: number, y: number): number {
  return (
    WIDE_MARKER |
    ((id & 0xff) << (WIDE_COORD_BITS * 2)) |
    ((y & WIDE_COORD_MASK) << WIDE_COORD_BITS) |
    (x & WIDE_COORD_MASK)
  ) >>> 0;
}

export function packTouchCancel(id: number): number {
  return (CANCEL_MARKER | ((id & 0xff) << 18)) >>> 0;
}

/** A child surface fact from the shell compositor, in desktop logical pixels. */
export interface SurfaceFrame {
  handle: number;
  /** Unclipped client rectangle [x, y, w, h]. */
  full: readonly [number, number, number, number];
  /** Visible part of it after shell clipping. */
  clip: readonly [number, number, number, number];
  focused: boolean;
  /** Painter order; larger paints later. */
  order: number;
}

function contains(rect: readonly [number, number, number, number], x: number, y: number): boolean {
  return x >= rect[0] && y >= rect[1] && x < rect[0] + rect[2] && y < rect[1] + rect[3];
}

/** Whether a desktop point lies on the visible part of a client rectangle. */
export function frameContains(frame: SurfaceFrame, x: number, y: number): boolean {
  return frame.full[2] > 0 && frame.full[3] > 0 && contains(frame.full, x, y) && contains(frame.clip, x, y);
}

/** Topmost surface whose visible client rectangle contains the point. */
export function frameAt(frames: readonly SurfaceFrame[], x: number, y: number): SurfaceFrame | null {
  let hit: SurfaceFrame | null = null;
  for (const frame of frames) {
    if (frameContains(frame, x, y) && (!hit || frame.order > hit.order)) hit = frame;
  }
  return hit;
}

/**
 * Desktop point to the app's logical viewport, clamped to its edges so a
 * contact that started inside keeps tracking when the pointer leaves the
 * window.
 */
export function toClient(
  frame: SurfaceFrame,
  viewport: readonly [number, number],
  x: number,
  y: number,
): [number, number] {
  const [left, top, width, height] = frame.full;
  const cx = width > 0 ? Math.floor(((x - left) * viewport[0]) / width) : 0;
  const cy = height > 0 ? Math.floor(((y - top) * viewport[1]) / height) : 0;
  return [Math.max(0, Math.min(viewport[0] - 1, cx)), Math.max(0, Math.min(viewport[1] - 1, cy))];
}

function isDevice(value: unknown): value is DeviceId {
  return typeof value === "string" && (DEVICE_IDS as readonly string[]).includes(value);
}

/** Parse one line an app sent. Anything that is not a StudioIntentLine is dropped. */
export function parseIntent(line: string): StudioIntentLine | null {
  let value: unknown;
  try {
    value = JSON.parse(line);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  switch (record.t) {
    case "play":
      if (typeof record.app !== "string") return null;
      if (record.device === undefined) return { t: "play", app: record.app };
      return isDevice(record.device) ? { t: "play", app: record.app, device: record.device } : null;
    case "skin":
      return isDevice(record.device) && typeof record.skin === "string"
        ? { t: "skin", device: record.device, skin: record.skin }
        : null;
    case "room":
      return typeof record.room === "string" ? { t: "room", room: record.room } : null;
    case "publish":
      return typeof record.app === "string" && typeof record.on === "boolean"
        ? { t: "publish", app: record.app, on: record.on }
        : null;
    case "membership":
      return { t: "membership" };
    case "opened":
      return LAPTOP_PROGRAMS.some((program) => program === record.program) ? { t: "opened", program: record.program as LaptopProgram } : null;
    case "signout":
      return { t: "signout" };
    case "theme":
      return isDesktopTheme(record.theme) ? { t: "theme", theme: record.theme } : null;
    default:
      return null;
  }
}
